import axios from 'core/common/config/axios-config';
import uniq = require("lodash/uniq");
import isEmpty = require("lodash/isEmpty");
import sum = require("lodash/sum");
import flatten = require("lodash/flatten");
import Grid, {GridLocationFieldDto} from 'core/grid/domain/grid';
import GridConfig from 'core/grid/domain/gridConfig';
import mdEntityService from 'core/metamodel/mdEntityService';
import {addTenantToQuery} from "core/common/utils/utils";
import {GridData} from "core/grid/domain/grid";
import {buildURL} from "core/common/utils/domUtils";
import {observable, ObservableMap, action} from "mobx";
import {EntityNameOrSpec} from "core/metamodel/mdEntityService";
import {GridRow} from "core/grid/domain/grid";
import {AxiosPromise, AxiosResponse} from "axios";
import {GridLocation} from "core/grid/domain/grid";
import {GridSpecialCondition} from "core/grid/domain/gridSpecialCondition";
import {IDomainObject, lovValue} from "core/form/formUtils";
import GridConfigCondition from "core/grid/domain/gridConfigCondition";
import {ID, SECURABLE_TYPE, TSECURABLE} from "core/metamodel/metaConstant";
import {MultiUpdateDto} from "core/grid/component/multiUpdateDialog";
import {ValidationMessage} from "core/common/domain/commonDomain";
import {LovItem, default as MdEntity} from "core/metamodel/mdEntity";
import {getItemFromLocalStorage, removeItemFromLocalStorage, setItemToLocalStorage} from "core/common/utils/storage";
import {GridActivityDto} from "core/event/eventDomain";
import securityService from "core/security/securityService";
import {PAGE} from "routes";
import {DocumentCountDto} from "core/document/documentDomain";
import {loc} from "core/common/config/locales";
import Chart from "core/grid/domain/chart";
import mdAxios from "core/common/config/axios-config";
import {GridBulkEntityDto} from "core/grid/gridUtils";

class GridService {


  @observable gridLocationMap: ObservableMap<GridLocation> = observable.shallowMap<GridLocation>(null);

  @observable gridMap: ObservableMap<Grid> = new ObservableMap<Grid>();

  @observable gridConfig: GridConfig;
  @observable gridLocationObject: GridLocation;
  @observable specialConditionObject: GridSpecialCondition;
  @observable gridLocationFieldObject: GridLocationFieldDto;


  /**
   * vlozi do GridStore objekty Grid pro gridLocations ktere tam dosud nebyly
   */
  @action fetchGrids(...gridLocations: string[]): Promise<ObservableMap<GridLocation>> {

    let unresolvedGridLocations = gridLocations.filter(v => !this.gridLocationMap.has(v));

    console.debug('gridService#fetchGrids unresolvedGridLocations = %o', unresolvedGridLocations);

    if (unresolvedGridLocations.length === 0) {
      return Promise.resolve(this.gridLocationMap);
    }

    let promiseArray: Promise<GridLocation[]>[] = [];

    let gridArrayFromCache = this.fetchGridsFromCache(unresolvedGridLocations, this.gridLocationMap);

    if (gridArrayFromCache.length) {
      promiseArray.push(Promise.resolve(gridArrayFromCache));
    }

    if (unresolvedGridLocations.length) {
      console.log("fetchGrids: still %s  unresolvedGridLocations to fetch from server", unresolvedGridLocations.length);
      let gridArrayFromServerPromise = axios.get('/core/grid-config', {params: {gridLocation: unresolvedGridLocations}}).then(response => response.data);
      promiseArray.push(gridArrayFromServerPromise);
    }

    let gridJsonArray: any[];

    return Promise.all(promiseArray).then( (arrayOfGridArrays) => {
      gridJsonArray = flatten<GridLocation>(arrayOfGridArrays);
      let entitiesToFetch: EntityNameOrSpec[] = gridJsonArray.filter(g => !!g.entityName).map(g => g.entityName);
      entitiesToFetch.push({entity: 'FILTEROPERATOR', lov: true});
      return mdEntityService.fetchEntities(uniq(entitiesToFetch));
    }).then(entityMap => {
        console.log("fetchGrids promise resolved: %O", gridJsonArray);

        const lang = loc.lang;

        for(let gridLocationJson of gridJsonArray) {
          let gridLocation = new GridLocation(entityMap.get(gridLocationJson.entityName), gridLocationJson);
          this.gridLocationMap.set(gridLocation.gridLocation, gridLocation);
          if (!gridLocation.$cached) {
            setItemToLocalStorage("GridLocation", lang, gridLocation.gridLocation, gridLocation);
          }

        }

        return this.gridLocationMap;
      });
  }

  @action initGrid(gridLocation: string): Grid {
    let gridLocationDto = this.gridLocationMap.get(gridLocation);
    if (!gridLocationDto) {
      return null;
    } else {
      let grid = new Grid(gridLocationDto);
      this.gridMap.set(gridLocation, grid);
      return grid;
    }
  }

  @action removeGrid(gridLocation: string): void {
    this.gridMap.delete(gridLocation);
  }

  getOrCreateGrid(gridLocation: string): Grid {
    let grid: Grid = this.gridMap.get(gridLocation);
    return (grid) ? grid : this.initGrid(gridLocation);
  }

  getGrid(gridLocation: string): Grid {
    return this.gridMap.get(gridLocation);
  }


  private fetchGridsFromCache(gridLocations: string[], gridLocationMap: ObservableMap<GridLocation>) : any[] {
    const lang = loc.lang;

    let oldSize = gridLocations.length;

    let gridArray: any[] = [];

    for(let i = gridLocations.length - 1; i >= 0; i--) {
        let gridLocation = gridLocations[i];

        if (gridLocationMap.has(gridLocation)) {
          continue;
        }
        let gridLocationStr =  getItemFromLocalStorage("GridLocation", lang, gridLocation);
        if (gridLocationStr) {
          let gridLoc: any = JSON.parse(gridLocationStr);
          gridLoc.$cached = true;
          gridArray.push(gridLoc);
          gridLocations.splice(i, 1);
        }
    }
    console.log("fetchGridsFromCache from %s requested gridLocations, %s found in cache, %s gridLocations to fetch from server",
      oldSize, gridArray.length, gridLocations.length);
    return gridArray;
  }



  /**
   * urceno pouze pro admin editaci gridLocation
   * @param gridLocation
   * @returns {*}
   */
  readGridLocation(gridLocation: string): Promise<GridLocation> {
    let gridLocationJson;

    return axios.get('/core/grid-location/' + gridLocation)
      .then((response: AxiosResponse) => {
        gridLocationJson = response.data;
        return mdEntityService.fetchEntities(['GridLocation', gridLocationJson.entityName]);
      }).then(entityMap => {
        return new GridLocation(mdEntityService.getEntity(gridLocationJson.entityName), gridLocationJson);
      });
  }

  /**
   * urceno pouze pro admin editaci gridLocationField
   * @param fieldKey
   * @returns {*}
   */
  readGridLocationField(fieldKey: string): Promise<GridLocationFieldDto> {
    return axios.get("/core/grid-location/field/" + fieldKey)
      .then((response) => new GridLocationFieldDto(response.data));
  }

  saveGridLocationFieldDto(gridLocationFieldObject: GridLocationFieldDto) {
    return axios.post('/core/grid-location/field', gridLocationFieldObject, {showPrettyError: true})
      .then((response) => response.data);
  }


  /**
   * urceno pouze pro admin editaci gridConfig
   * @param gridId
   * @returns - gridConfig
   */
  readGridConfig(gridId): Promise<GridConfig> {

    let gridConfigJson: any;
    return axios.get('/core/grid-config/' + gridId)
      .then((response) => {
        gridConfigJson = response.data;
        return Promise.all([mdEntityService.fetchEntities([{entity: 'FILTEROPERATOR', lov: true}, gridConfigJson.entityName]), this.fetchGrids(gridConfigJson.gridLocation)]) as any;
      }).then( ([entityMap, gridLocationMap]: [ObservableMap<MdEntity>, ObservableMap<GridLocation>]) => {
        return new GridConfig(this.gridLocationMap.get(gridConfigJson.gridLocation), gridConfigJson);
      });
  }

  readSpecialCondition(specialConditionId: number): Promise<GridSpecialCondition> {
    return axios.get('/core/grid-special-condition/' + specialConditionId)
      .then(response => new GridSpecialCondition(response.data));
  }

  saveSpecialCondition(specialCondition: GridSpecialCondition): Promise<IDomainObject> {
    return axios.post('/core/grid-special-condition', specialCondition)
      .then((response) => response.data);
  }

  deleteSpecialCondition(specialConditionId: number): Promise<any> {
    return axios.delete('/core/grid-special-condition/' + specialConditionId, {showPrettyError: true});
  }


  /**
   * fetchne entity s Lovs se seznamy hodnot v conditions.
   * Side effect - vola {@link MdEntityService#fetchEntities()}, takze je ulozi do #metamodel.entities
   * @param conditions
   * @returns {Promise.<entityObject>}
     */
  fetchConditionLovs(conditions: GridConfigCondition[]) {
    let valueSources: EntityNameOrSpec[] = uniq<EntityNameOrSpec>(conditions.filter(c => (c.field && c.field.hasLocalValueSource())).map(c => ({
      entity: c.field.valueSource,
      lov: true
    })));
    return mdEntityService.fetchEntities(valueSources);
  }

  /*
   * Spocita width, min-width a max-width pro sloupce gridu.
   *
   * return Array[widths, min-widths, max-widths]
   */
  computeGridWidths(data: GridData, gridConfig: GridConfig) : number[][] {
    //console.time("computeGridWidths");
    // maximalni hodnota, kterou muze nabyvat min-width
    let MAXIMAL_COLUMN_MIN_WIDTH_PX = gridConfig.maxColumnWidth;
    // hodnata pro max-width sloupcu
    let MAXIMAL_COLUMN_WIDTH_PX = 7000;
    // retezec, ktery se doplni z text headeru pro vypocet sirky (zastupuje symobl razeni a filtru)
    let HEADER_ADDITIONAL_STRING = 'AA';
    // sirka pripocitavana ke spocitane sirce sloupce (aby text nebyl od uplneho kraje k uplnemu kraji)
    let CELL_PADDING_PX = 5;

    let matrix: string[] = [];
    let headers = gridConfig.columns.map((gcCol) => {
      let v = gcCol.gridHeaderLabelActive;
      return ((v) ? v.trim() : "") + HEADER_ADDITIONAL_STRING;
    });

    matrix.push.apply(matrix, headers);
    //console.log("Array: %o", matrix);

    if (data) {
      for(let rows of data.pageMap.values()) {
        for (let row of rows) {
          for (let i = 0; i < row.cells.length; i++) {
            let cellValue = row.cells[i].value;
            let str = (cellValue) ? ( (typeof cellValue == 'string') ? (cellValue as string).trim() : row.cells[i].value.toString().trim() ) : "";
            if (matrix[i].length < str.length) {
              matrix[i] = str;
            }
          }
        }
      }
    }


    //let absoluteLengths = _.zip(...matrix).map(col => _.max(col));

    // let absoluteMax = sum(absoluteLengths);

    // puvodni vypocet sirky sloupcu - podle nejdelsich textu
    // let gridWidths = absoluteLengths.map(v => Math.round(10000 * v / absoluteMax)/100 + "%");

    // vypocet min-width pro sloupce - pomoci fiktivnich DIVu
    // horni hranice pro min-width je MAXIMAL_COLUMN_MAX_WIDTH_PX
    let gridMinWidthsPX = matrix.map(v => {
      let elemDiv = document.createElement('div');
      elemDiv.style.cssText = 'font-family:\'Roboto Condensed\',\'Helvetica Neue\',Helvetica,Arial,sans-serif;font-size:13px;position:absolute;left:0px;top:0px;z-index:0;visibility:hidden;';
      elemDiv.textContent = v;
      document.body.appendChild(elemDiv);
      let elemWidth = elemDiv.clientWidth + CELL_PADDING_PX;
      document.body.removeChild(elemDiv);
      return elemWidth < MAXIMAL_COLUMN_MIN_WIDTH_PX ? elemWidth : MAXIMAL_COLUMN_MIN_WIDTH_PX;
    });

    // udelame sumu minWidths
    let sumGridMinWidths = sum(gridMinWidthsPX);
    // spocitame width pro sloupce tak, aby odpovidaly pomerum min-width
    let gridWidthsPrct = gridMinWidthsPX.map(v => ((100 * v) / sumGridMinWidths));

    // spocitame maxWidths (jen vytvorime array)
    let gridMaxWidthsPX = Array.from(new Array(gridWidthsPrct.length), () => MAXIMAL_COLUMN_WIDTH_PX);

    //console.timeEnd("computeGridWidths");
    //  console.debug('computeGridWidths: %O', gridWidthsPrct);
    //  console.debug('computeGridMinWidths: %O', gridMinWidthsPX);
    //  console.debug('computeGridMaxWidths: %O', gridMaxWidthsPX);

    return [gridWidthsPrct, gridMinWidthsPX, gridMaxWidthsPX];
  }


  validateCondition(condition: GridConfigCondition, errorMessages: ValidationMessage[], index: number, allOperators: LovItem[]) {
    if (!condition.field || !condition.operator) {
      errorMessages.push({message: (index + 1) + ". Filter must have column and operator"});
      return false;
    }
    let operatorLov: LovItem & {inputCount?: string|number} = allOperators.find(li => li.value === condition.operator);
    let cardinality = operatorLov.inputCount;
    if (cardinality == 1 || cardinality == "N") {
      if (isEmpty(condition.values) || condition.values[0] == null) {
        errorMessages.push({message: (index + 1) + ". Filter must have value"});
        return false;
      }
    } else if (cardinality == 2) {
      if (isEmpty(condition.values) || condition.values.length < 2 || condition.values[0] == null || condition.values[1] == null ) {
        errorMessages.push({message: (index + 1) + ". Filter must have two values"});
        return false;
      }
    }
    return true;
  }

  validateSortColumn(sortColumns, sortColumn, errorMessages, index) {
    if (!sortColumn.field || !sortColumn.sortOrder) {
      errorMessages.push({message: (index + 1) + ". Grid ordering must have column and direction"});
      return false;
    }

    if (sortColumn.fixed) {
      if (sortColumns.filter((v, i) => ( !v.fixed && i < index )).length > 0) {
        errorMessages.push({message: " Fixed ordering must be at the beginning of the ordering list (eg. It is not allowed to have first ordering non fixed and second fixed."});
        return false;
      }
    }

    return true;
  }


  computeConfigLevels(gridLocation: GridLocation, gridConfig: GridConfig) : {configlevelOptions: LovItem[], disabled: boolean} {
    let disabled = !gridConfig || gridConfig.gridId < 0;

    let configlevelOptions = mdEntityService.getEntity("CONFIGLEVEL").lovItems;
    let securableId: TSECURABLE = lovValue(gridLocation.securable);
    if (securableId) {
      let mapa = {GLOBAL: "PublicGrid", TENANT: "TenantGrid", TENANTGROUP: "TenantGroupGrid"};
      let permissionList = securityService.permissionList(securableId);
      let currentClValue = (gridConfig) ? lovValue(gridConfig.configLevel) : null;
      configlevelOptions = configlevelOptions.filter(lov => {
        let clValue = lovValue(lov);
        let requiredPermission = mapa[clValue];
        return !requiredPermission || (permissionList && permissionList.includes(requiredPermission) || clValue === currentClValue );
      });
      let requiredPermission = mapa[currentClValue];
      disabled = disabled || (requiredPermission && !permissionList.includes(requiredPermission));
    }

    return {configlevelOptions, disabled};
  }

  @action getRowIds(grid: Grid, firstRows: number): Promise<number[]> {
    let params = grid.createParams();
    params.page = 1;

    return axios.get(`/core/grid/${grid.activeGridConfig.gridId}/rows-ids/${firstRows}`, {params})
      .then((response) => response.data);
  }

  innerSearch(gridId:number, params: any) : AxiosPromise {
    return axios.get('/core/grid/' +gridId, {params});
  }

  @action search(grid: Grid, refresh: boolean = true, page: number = 1) {

    let params = grid.createParams();
    params.page = page;

    console.debug("%c running search with gridLocation = %s, gridId = %s, searchTerm = %s, masterId = %s, params = %O", "background-color: green", grid.gridLocation, grid.activeGridConfig.gridId, grid.searchTerm, grid.masterId, params);

    let _gridData: any;

    return this.innerSearch(grid.activeGridConfig.gridId, params)
      .then(action("gridService-search-callback-1", (response: AxiosResponse) => {

        _gridData = response.data;

        if (refresh || !grid.data) {
          grid.data = new GridData(grid, response.data);
        }



        //gridConfig cache busting
        if (grid.data.configDateCreated === grid.activeGridConfig.dateCreated) {
          return Promise.resolve(grid);
        } else {
          console.log('Timestamp of configDateCreated (' + grid.data.configDateCreated + ') is different than date in cache (' + grid.activeGridConfig.dateCreated + ') => refetching deleting from cache');
          return this.readGridConfig(grid.activeGridConfig.gridId)
            .then(action("gridService-grid-config-callback", (newGridConfig:GridConfig) => {
              newGridConfig.gridWidths = gridService.computeGridWidths(null, newGridConfig);
              grid.assignActiveGridConfig(newGridConfig);
              grid.replaceGridConfig(newGridConfig);
              this.gridLocationMap.get(grid.gridLocation).replaceGridConfig(newGridConfig);
              removeItemFromLocalStorage("GridLocation", loc.lang, grid.gridLocation);
              return grid;
            }));
        }
      }))
      .then(action("gridService-search-callback-2", response => {

        grid.loading = false;


        let page: number = (_gridData.page != null) ? _gridData.page : 1;
        grid.data.initData(page, _gridData.rows, _gridData.aggregateRows);

        grid.gridWidths = this.computeGridWidths(grid.data, grid.activeGridConfig);

        return grid;

      }), (err) => {
        grid.loading = false;
        throw err;
      })
      ;
  }


  @action chart(grid: Grid, customColumns?: string[]): Promise<Chart> {
    let params = grid.createFullParams();
    let rowIds = params.rowid;
    params.rowid = undefined;
    return axios.post('/core/grid/' + grid.activeGridConfig.gridId + "/chart", {rowIds , customColumns}, {params})
      .then(action("gridService-chart-callback-1", (response: AxiosResponse) => {
        return new Chart(response.data);
      }));
  }

  exportUrl(grid, format, inline) {
    let params = grid.createFullParams();
    params.inline = inline;
    return buildURL('/api/core/grid/export/' + format + '/' + grid.activeGridConfig.gridId, addTenantToQuery(params));
  }

  documentExportUrl(grid, documentTypeId, inline): string {
    let params = grid.createFullParams();
    params.inline = inline;
    return buildURL('/api/core/grid/generate-document/' + documentTypeId + '/' + grid.activeGridConfig.gridId, addTenantToQuery(params));
  }

  documentDownloadUrl(grid, documentLocation, documentCountObject: DocumentCountDto, inline): string {
    let finalParams = grid.createFullParams(documentCountObject.gridParams);
    finalParams.inline = inline;
    finalParams.duplexPrinting = documentCountObject.duplexPrinting;
    return buildURL('/api/core/grid/download-document/' + documentLocation + '/' + documentCountObject.selectedFormat +  '/' + grid.activeGridConfig.gridId, addTenantToQuery(finalParams));
  }



  setDefault(gridId: number, gridLocation: string) {
    return axios.post('/core/grid-config/set-default', {gridId, gridLocation});
  }

  formatRow(gridId, rowObject, paramSpec): Promise<GridRow> {
    return axios.post('/core/grid/format-row/' + gridId, rowObject, {params:paramSpec})
    .then(response => response.data);
  }


  multiUpdate(multiUpdateDto: MultiUpdateDto): Promise<MultiUpdateDto> {
    return axios.post('/core/grid-multi-update', multiUpdateDto)
      .then((response) => new MultiUpdateDto(response.data));
  }

  generateActivity(grid: Grid, gridActivityDto: GridActivityDto, part: number): Promise<GridActivityDto> {
    return axios.post('/core/grid/generate-activity/' + part, gridActivityDto, {showPrettyError: true})
      .then(response => new GridActivityDto(response.data));
  }

  getColumnsFromSql(sqlCommand: string, gridLocation: GridLocation): Promise<GridConfig> {
    return axios.post('/core/grid-config/parse-sql', {sqlCommand, "gridLocation": gridLocation.gridLocation}, {showPrettyError: true})
      .then((response) => new GridConfig(gridLocation, response.data));
  }

  doMultipleEmail(gridBulkEntityDto: GridBulkEntityDto, entityName: string, part: number): Promise<GridBulkEntityDto> {
    return mdAxios.post('/core/grid/email-multiple/' + entityName + "/" + part, gridBulkEntityDto, {showPrettyError: true})
      .then(response => new GridBulkEntityDto(response.data));
  }


}

const gridService = new GridService();
export default gridService;




// WEBPACK FOOTER //
// ./src/core/grid/gridService.ts